import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Users, 
  UserPlus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, MoreVertical, 
  Shield, 
  Phone, 
  CheckCircle,
  RefreshCw,
  Download,
  Key,
  UserCheck
} from 'lucide-react';
import PermissionEditor from './PermissionEditor';

// CR15 — account status is more than a binary Active/Inactive now: a user can be
// deactivated (reversible, no timer), mid-30-day-deletion-grace-period (still
// reversible), or anonymized (permanent, cannot be undone).
function getAccountStatus(user) {
  if (user.anonymized_at) {
    return { label: 'Anonymized', badgeClass: 'bg-gray-200 text-gray-600', canReactivate: false };
  }
  if (user.deletion_requested_at) {
    const daysElapsed = Math.floor((Date.now() - new Date(user.deletion_requested_at).getTime()) / 86400000);
    const daysLeft = Math.max(0, 30 - daysElapsed);
    return { label: `Pending Deletion (${daysLeft}d left)`, badgeClass: 'bg-orange-100 text-orange-800', canReactivate: true };
  }
  if (user.is_active === false) {
    return { label: 'Deactivated', badgeClass: 'bg-red-100 text-red-800', canReactivate: true };
  }
  return { label: 'Active', badgeClass: 'bg-green-100 text-green-800', canReactivate: false };
}

const UserManagementSystem = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [openActionsFor, setOpenActionsFor] = useState(null);
  const [actionsMenuPos, setActionsMenuPos] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    role: 'customer'
  });
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [permEditUser, setPermEditUser] = useState(null);

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch("/api/users-fixed", {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch("/api/users/roles", {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setRoles(data);
      }
    } catch (error) {
      console.error("Error loading roles:", error);
    }
  };

  const handleCreateUser = async () => {
    setErrors({});
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch("/api/users/create", {
        method: 'POST',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSuccessMessage('User created successfully!');
        setShowModal(false);
        resetForm();
        loadUsers();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error || 'Failed to create user' });
      }
    } catch (error) {
      setErrors({ general: 'Network error occurred' });
    }
  };

  const handleUpdateUser = async () => {
    setErrors({});
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/api/users/${selectedUser.id}/update`, {
        method: 'PUT',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSuccessMessage('User updated successfully!');
        setShowModal(false);
        resetForm();
        loadUsers();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ general: result.error || 'Failed to update user' });
      }
    } catch (error) {
      setErrors({ general: 'Network error occurred' });
    }
  };

  const handlePasswordChange = async () => {
    setErrors({});
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrors({ password: 'Passwords do not match' });
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      setErrors({ password: 'Password must be at least 6 characters long' });
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/api/users/${selectedUser.id}/password`, {
        method: 'PUT',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ newPassword: passwordData.newPassword })
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSuccessMessage('Password updated successfully!');
        setShowModal(false);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setErrors({ password: result.error || 'Failed to update password' });
      }
    } catch (error) {
      setErrors({ password: 'Network error occurred' });
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) {
      return;
    }

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`/api/users/${userId}/delete`, {
        method: 'DELETE',
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setSuccessMessage('User deactivated successfully!');
        await loadUsers();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        alert(result.error || 'Failed to delete user');
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      alert('Network error occurred');
    }
  };

  const reactivateUser = async (userId) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`/api/users/${userId}/reactivate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setSuccessMessage('User reactivated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        loadUsers();
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to reactivate user');
      }
    } catch (error) {
      alert('Network error occurred');
    }
  };

  const handleExportUsers = async () => {
    try {
      const usersToExport = filteredUsers;
      
      const csvContent = [
        'ID,Email,First Name,Last Name,Phone,Role,Status,Created Date',
        ...usersToExport.map(user => [
          user.id,
          user.email,
          user.first_name || '',
          user.last_name || '',
          user.phone || '',
          user.role || '',
          user.is_active !== false ? 'Active' : 'Inactive',
          user.created_at ? new Date(user.created_at).toLocaleDateString() : ''
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setSuccessMessage('Users exported successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      alert('Failed to export users');
    }
  };

  useEffect(() => {
    if (openActionsFor === null) return;
    const onClick = (e) => {
      if (e.target.closest('[data-action-toggle]')) return;
      setOpenActionsFor(null);
      setActionsMenuPos(null);
    };
    document.addEventListener('click', onClick); // actionMenuClickOutside
    return () => document.removeEventListener('click', onClick);
  }, [openActionsFor]);

  const viewUserDetails = async (user) => {
    setUserDetails(null);
    setLoadingDetails(true);
    setShowDetailsModal(true);
    try {
      const res = await fetch('/api/users/' + user.id + '/full-details', {
        headers: { 'Authorization': 'Bearer ' + localStorage.getItem('authToken') }
      });
      if (res.ok) {
        const data = await res.json();
        setUserDetails(data);
      } else {
        setUserDetails({ error: true });
      }
    } catch (e) {
      setUserDetails({ error: true });
    } finally {
      setLoadingDetails(false);
    }
  };

  const openModal = (type, user = null) => {
    setModalType(type);
    setSelectedUser(user);
    setErrors({});
    
    if (type === 'create') {
      resetForm();
    } else if (type === 'edit' && user) {
      setFormData({
        email: user.email || '',
        password: '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        role: user.role || 'customer'
      });
    } else if (type === 'password' && user) {
      setPasswordData({ newPassword: '', confirmPassword: '' });
    }
    
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      role: 'customer'
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (modalType === 'password') {
      handlePasswordChange();
      return;
    }
    
    const newErrors = {};
    if (!formData.email) newErrors.email = 'Email is required';
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (modalType === 'create' && !formData.password) newErrors.password = 'Password is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (modalType === 'create') {
      handleCreateUser();
    } else if (modalType === 'edit') {
      handleUpdateUser();
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === '' || user.role === roleFilter;
    
    const matchesStatus = statusFilter === '' || 
      (statusFilter === 'active' && user.is_active !== false) ||
      (statusFilter === 'inactive' && user.is_active === false);
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-100 text-purple-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'operator': return 'bg-green-100 text-green-800';
      case 'customer': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Shield className="w-4 h-4" />;
      case 'admin': return <UserCheck className="w-4 h-4" />;
      case 'operator': return <Eye className="w-4 h-4" />;
      case 'customer': return <Users className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {successMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-800">{successMessage}</span>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
              <p className="text-sm text-gray-600">Manage users, roles, and permissions</p>
            </div>
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => openModal('create')}
                className="flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add User
              </button>
              <button 
                onClick={loadUsers}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Roles</option>
              {roles.map((role) => (
                <option key={role.name} value={role.name}>
                  {role.display_name}
                </option>
              ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowFiltersModal(true)}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <Filter className="w-4 h-4 mr-2" />
                More Filters
              </button>
              <button 
                onClick={handleExportUsers}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100"
              >
                <Download className="w-4 h-4 mr-2" />
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Users ({filteredUsers.length})
            </h3>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading users...</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {user.first_name?.charAt(0)}{user.last_name?.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-sm text-gray-500">{user.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}>
                          {getRoleIcon(user.role)}
                          <span className="ml-1">{user.role?.replace('_', ' ')}</span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {user.phone && (
                            <div className="flex items-center">
                              <Phone className="w-4 h-4 text-gray-400 mr-1" />
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getAccountStatus(user).badgeClass}`}>
                          {getAccountStatus(user).label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative inline-block text-left">
                          <button
                            data-action-toggle
                            onClick={(e) => {
                              if (openActionsFor === user.id) {
                                setOpenActionsFor(null);
                                setActionsMenuPos(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setActionsMenuPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 180) });
                                setOpenActionsFor(user.id);
                              }
                            }}
                            className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-gray-100 text-gray-600"
                            title="Actions"
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>
                          {openActionsFor === user.id && actionsMenuPos && createPortal(
                            <div
                              data-action-toggle
                              style={{ position: 'fixed', top: actionsMenuPos.top, left: actionsMenuPos.left }}
                              className="bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[180px] overflow-hidden"
                            >
                              <button onClick={() => { setOpenActionsFor(null); viewUserDetails(user); }}
                                      className="w-full px-3 py-2.5 text-left text-sm hover:bg-indigo-50 flex items-center text-indigo-600">
                                <Eye className="w-4 h-4 mr-2" /> View Details
                              </button>
                              <button onClick={() => { setOpenActionsFor(null); openModal('edit', user); }}
                                      className="w-full px-3 py-2.5 text-left text-sm hover:bg-blue-50 flex items-center text-blue-600 border-t border-gray-100">
                                <Edit className="w-4 h-4 mr-2" /> Edit User
                              </button>
                              <button onClick={() => { setOpenActionsFor(null); openModal('password', user); }}
                                      className="w-full px-3 py-2.5 text-left text-sm hover:bg-yellow-50 flex items-center text-yellow-600 border-t border-gray-100">
                                <Key className="w-4 h-4 mr-2" /> Change Password
                              </button>
                              <button onClick={() => { setOpenActionsFor(null); setPermEditUser(user); }}
                                      className="w-full px-3 py-2.5 text-left text-sm hover:bg-purple-50 flex items-center text-purple-600 border-t border-gray-100">
                                <Shield className="w-4 h-4 mr-2" /> Permissions
                              </button>
                              {getAccountStatus(user).canReactivate ? (
                                <button onClick={() => { setOpenActionsFor(null); reactivateUser(user.id); }}
                                        className="w-full px-3 py-2.5 text-left text-sm hover:bg-green-50 flex items-center text-green-600 border-t border-gray-100">
                                  <RefreshCw className="w-4 h-4 mr-2" /> {user.deletion_requested_at ? 'Cancel Deletion' : 'Reactivate'}
                                </button>
                              ) : user.anonymized_at ? (
                                <div className="w-full px-3 py-2.5 text-left text-sm text-gray-400 border-t border-gray-100">
                                  Permanently deleted
                                </div>
                              ) : (
                                <button onClick={() => { setOpenActionsFor(null); handleDeleteUser(user.id); }}
                                        className="w-full px-3 py-2.5 text-left text-sm hover:bg-red-50 flex items-center text-red-600 border-t border-gray-100">
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </button>
                              )}
                            </div>,
                            document.body
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showDetailsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                <h3 className="text-lg font-bold text-gray-800">
                  {loadingDetails ? 'Loading user details…' : (userDetails && userDetails.user ? userDetails.user.first_name + ' ' + userDetails.user.last_name : 'User Details')}
                </h3>
                <button onClick={() => { setShowDetailsModal(false); setUserDetails(null); }} className="text-gray-400 hover:text-gray-600 text-2xl leading-none px-2">×</button>
              </div>
              
              {loadingDetails ? (
                <div className="p-12 text-center text-gray-400">Loading…</div>
              ) : !userDetails || userDetails.error ? (
                <div className="p-12 text-center text-gray-400">Could not load user details</div>
              ) : (
                <div className="p-6 space-y-5">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Account</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-gray-500">Email:</span> <span className="font-medium">{userDetails.user.email}</span></div>
                      <div><span className="text-gray-500">Phone:</span> <span className="font-medium">{userDetails.user.phone || '—'}</span></div>
                      <div><span className="text-gray-500">Role:</span> <span className="font-medium capitalize">{userDetails.user.role || 'customer'}</span></div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-gray-500">Status:</span>
                        <span className={'inline-flex px-2 py-0.5 text-xs rounded-full ' + getAccountStatus(userDetails.user).badgeClass}>
                          {getAccountStatus(userDetails.user).label}
                        </span>
                        {userDetails.user.phone_verified && <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700">Verified</span>}
                      </div>
                      <div><span className="text-gray-500">Joined:</span> <span className="font-medium">{userDetails.user.created_at ? new Date(userDetails.user.created_at).toLocaleDateString() : '—'}</span></div>
                      <div><span className="text-gray-500">Last login:</span> <span className="font-medium">{userDetails.user.last_login ? new Date(userDetails.user.last_login).toLocaleDateString() : 'Never'}</span></div>
                    </div>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Activity</h4>
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div>
                        <div className="text-2xl font-bold text-gray-800">{userDetails.stats.total_sessions || 0}</div>
                        <div className="text-xs text-gray-500">Sessions</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-800">{parseFloat(userDetails.stats.total_kwh || 0).toFixed(1)}</div>
                        <div className="text-xs text-gray-500">kWh</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-gray-800">£{parseFloat(userDetails.stats.total_spent || 0).toFixed(2)}</div>
                        <div className="text-xs text-gray-500">Spent</div>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-gray-800">{userDetails.stats.last_session_at ? new Date(userDetails.stats.last_session_at).toLocaleDateString() : 'Never'}</div>
                        <div className="text-xs text-gray-500">Last session</div>
                      </div>
                    </div>
                  </div>
                  
                  {userDetails.sessions && userDetails.sessions.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent Sessions</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left px-3 py-2 text-xs text-gray-500 font-medium">Date</th>
                              <th className="text-left px-3 py-2 text-xs text-gray-500 font-medium">Charger</th>
                              <th className="text-right px-3 py-2 text-xs text-gray-500 font-medium">Energy</th>
                              <th className="text-left px-3 py-2 text-xs text-gray-500 font-medium">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userDetails.sessions.map(s => (
                              <tr key={s.id} className="border-t">
                                <td className="px-3 py-2 text-gray-600">{new Date(s.start_time).toLocaleDateString()}</td>
                                <td className="px-3 py-2">{s.charger_name || '—'}</td>
                                <td className="px-3 py-2 text-right">{parseFloat(s.energy_consumed || 0).toFixed(1)} kWh</td>
                                <td className="px-3 py-2">
                                  <span className={'inline-flex px-2 py-0.5 text-xs rounded-full ' + (s.status === 'completed' ? 'bg-green-100 text-green-700' : s.status === 'active' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700')}>{s.status}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {userDetails.home_charger && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">🏠 Home Charger</h4>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div><span className="text-gray-500">Name:</span> <span className="font-medium">{userDetails.home_charger.nickname}</span></div>
                        <div><span className="text-gray-500">Serial:</span> <span className="font-medium font-mono text-xs">{userDetails.home_charger.serial_number}</span></div>
                        <div><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{userDetails.home_charger.charger_type}</span></div>
                      </div>
                    </div>
                  )}
                  
                  {userDetails.b2b && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">🏢 B2B Membership</h4>
                      <div className="grid grid-cols-3 gap-2 text-sm">
                        <div><span className="text-gray-500">Company:</span> <span className="font-medium">{userDetails.b2b.company_name}</span></div>
                        <div><span className="text-gray-500">Role:</span> <span className="font-medium capitalize">{userDetails.b2b.company_role}</span></div>
                        <div><span className="text-gray-500">Status:</span> <span className="font-medium capitalize">{userDetails.b2b.status}</span></div>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Account Flags</h4>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className={'px-2 py-1 rounded-full ' + (userDetails.user.has_push_token ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500')}>
                        {userDetails.user.has_push_token ? '📱 Push enabled' : '📱 No push token'}
                      </span>
                      <span className={'px-2 py-1 rounded-full ' + (userDetails.user.has_active_card ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500')}>
                        {userDetails.user.has_active_card ? '💳 Payment on file' : '💳 No payment method'}
                      </span>
                      {userDetails.settings && userDetails.settings.default_charging_card_id && (
                        <span className="px-2 py-1 rounded-full bg-amber-100 text-amber-700">⭐ Default card set</span>
                      )}
                      {userDetails.settings && userDetails.settings.auto_start_charging && (
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700">⚡ Auto-start enabled</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end">
                <button onClick={() => { setShowDetailsModal(false); setUserDetails(null); }} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium">Close</button>
              </div>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {modalType === 'create' ? 'Create New User' : 
                   modalType === 'edit' ? 'Edit User' : 
                   modalType === 'password' ? 'Change Password' : 'User Details'}
                </h3>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                {errors.general && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <span className="text-red-800 text-sm">{errors.general}</span>
                  </div>
                )}
                
                {modalType === 'password' ? (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        New Password for {selectedUser?.first_name} {selectedUser?.last_name}
                      </label>
                      <input
                        type="password"
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter new password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
                      <input
                        type="password"
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Confirm new password"
                      />
                      {errors.password && <span className="text-red-500 text-xs">{errors.password}</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      {errors.email && <span className="text-red-500 text-xs">{errors.email}</span>}
                    </div>
                    
                    {modalType === 'create' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                        <input
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {errors.password && <span className="text-red-500 text-xs">{errors.password}</span>}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                        <input
                          type="text"
                          value={formData.first_name}
                          onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {errors.first_name && <span className="text-red-500 text-xs">{errors.first_name}</span>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                        <input
                          type="text"
                          value={formData.last_name}
                          onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        {errors.last_name && <span className="text-red-500 text-xs">{errors.last_name}</span>}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    {modalType === 'create' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                        <select
                          value={formData.role}
                          onChange={(e) => setFormData({...formData, role: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {roles.map((role) => (
                            <option key={role.name} value={role.name}>
                              {role.display_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {modalType === 'edit' && (
                      <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
                        To change this user's role or page access, use the <strong>🔑 Permissions</strong> button on the Users page.
                      </p>
                    )}
                  </>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                  >
                    {modalType === 'create' ? 'Create User' : 
                     modalType === 'edit' ? 'Update User' :
                     modalType === 'password' ? 'Change Password' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showFiltersModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
              <h3 className="text-lg font-semibold mb-4">Advanced Filters</h3>
              <p className="text-gray-600 mb-4">Additional filtering options will be available here.</p>
              <button
                onClick={() => setShowFiltersModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
      {permEditUser && (
        <PermissionEditor
          user={permEditUser}
          currentUser={JSON.parse(localStorage.getItem('currentUser') || '{}')}
          onClose={() => setPermEditUser(null)}
          onSaved={() => { setPermEditUser(null); loadUsers(); }}
        />
      )}
    </div>
  );
};

export default UserManagementSystem;
